//--------------------------------------------------
// imported classes
//--------------------------------------------------
const CityRegionConvertor = require('@/tbs/model/vo/CityRegion')
const DataConvertor = require('@/tbs/core/DataConvertor')
const ClassFunc = require('@/tbs/util/ClassFunc')
const ObjectFunc = require('@/tbs/util/ObjectFunc')
const GlobalFunc = require('@/tbs/util/GlobalFunc')
const AbstractMediator = require('@/tbs/core/AbstractMediator')
const ModelLocator = require('@/tbs/model/ModelLocator')

const Staff = require('@/tbs/model/vo/Staff')
const DSBridge = require('@/libs/ds-bridge')
const Utils = require('@/utils')

/**
 * The BaseMediator class works as the basic mediator to maintain
 * the business logic for the scene instance.
 * It provides the basic features for the resource CRUD operations.
 * It would query the data via the service if list condition.
 * It would fetch the data via the service if not create condition.
 * It would submit the data via the event if create or update condition.
 * It would delete the data via the event if delete condition.
 */
const tbs_controller_BaseMediator = function() {
    //--------------------------------------------------
    // super method for the base class
    //--------------------------------------------------
    ClassFunc.super(this, AbstractMediator, arguments)
    // // console.log('tbs_controller_BaseMediator constructor')
}

//--------------------------------------------------
// prototype
//--------------------------------------------------
tbs_controller_BaseMediator.prototype = {
    //--------------------------------------------------
    // api method
    //--------------------------------------------------
    onCreationComplete: function() {
        this.handleCreationComplete()
    },
    /**
     * @api
     * Handle the creation complete condition triggered by 
     * the scene instance.
     */
    handleCreationComplete: function() {
        // // console.log('handleCreationComplete')
        if (!this._bridge.isCreate()) {
            this.creationComplete()
        }
    },
    /**
     * @api
     * Handle the custom event condition triggered by 
     * the scene instance.
     * @event The event object contains 
     * {type:String, data:Object, target:VueComponent}
     */
    handleCustomEvent: function(event) {
        // console.log('handleCustomEvent', event)
        // use the customized event handler within the specific mediator
        // if event is handled with null return value, skip the following handler
        if (this.handleCustomizedEvent) {
            if (!this.handleCustomizedEvent(event)) {
                return
            }
        }
        if (event.type == 'save') {
            this.submitData(event.target, event.data, true)
        }
        else if (event.type == 'submit') {
            this.submitData(event.target, event.data)
        }
        else if (event.type == 'delete') {
            this.deleteRecord(event.target, event.data)
        }
        else if (event.type == 'navigate') {
            if (event.data.name) {
                this._bridge.goToScene(event.data.name, event.data.params, event.data.query, event.data.isReplace)
            }
            else if (event.data.path) {
                this._bridge.goToPath(event.data.path, event.data.query)
            }
            else if (event.data.url) {
                this._bridge.goToUrl(event.data.url)
            }
            else if (event.data.action) {
                if (event.data.action == 'exit') {
                    this.exitPage()
                }
            }
        }
        else if (event.type == 'invite') {
            this.sendStaffInvite(event.data)
        }
    },
    
    //--------------------------------------------------
    // private method
    //--------------------------------------------------
    /**
     * @private
     * Try to render the component according to the service data.
     * Here's the main steps:
     * Load the entity object via the service API request.
     * Convert the entity object into the view object according to the
     * declared view object model.
     * Set the bindable data model via the wrapper for the vue component.
     */
    creationComplete: function(ctx) {
        const self = this
        // console.log('@creationComplete', ctx, self._bridge.isList(), self._bridge.isManage(), self._bridge.getRouteName())  
        const convertor = self.p_convertor()
        const wrapper = DataConvertor.wrapper(convertor)
        let type 
        type = self._bridge.isList() ? 'query' : 'get'
        type = self._bridge.isManage() ? 'manage' : type
        const source = convertor.view[type]
        const service = type + ObjectFunc.capitalizeFirstLetter(wrapper)
        // console.log('creationComplete', type, service, source)
        if (!ctx) {
            ctx = {parameters: {}}
        }
        else if (!ctx.parameters) {
            ctx.parameters = {}
        }
        let parameters = ctx.parameters
        const routeParameters = {}
        ObjectFunc.mergeObject(routeParameters, self._bridge.getRouteParam())
        ObjectFunc.mergeObject(routeParameters, self._bridge.getRouteQuery())
        parameters = ObjectFunc.mergeObject(routeParameters, parameters)
        if (self._bridge.isEdit()) {
            self.target().isEdit = true
        }
        // console.log('@parameters before data load', parameters)
        parameters = this.handleDataLoad(parameters)
        // console.log('@parameters after data load', parameters, ClassFunc.getClass(source))
        parameters = DataConvertor.queryTransform(convertor, parameters, type)
        // console.log('@parameters after query transform', parameters, type)
        if (ClassFunc.getClass(source) == 'Array') {
            self._bridge.addResourceListener(service, function(e) {
                self._bridge.removeResourceListener(service, e.handler)
                // console.log('query.data', service, e.data)
                self.handleQueryCollectionService(e)
            })
            self._bridge.callResourceInstance(service, {
                services: ObjectFunc.extractProperties(source, ['baseUrl', 'apiPrefix', 'apiSuffix', 'service', 'seq']),
                method: 'GET',
                parameters: parameters
            }, true)
        }
        else {
            self._bridge.addResourceListener(service, function(e) {
                self._bridge.removeResourceListener(service, e.handler)
                // console.log('query.data', service, e.data)
                self.handleQueryService(e)
            })
            // console.log('@parameters', parameters)
            if (self._bridge.isDetail() && parameters.id == null) {
                parameters.id = self._bridge.getRouteParam('id')
            }
            if (source.apiSuffix && source.apiSuffix.indexOf('bmb-resource-service') > -1 && type == 'get') {
                self._bridge.callResourceInstance(service, {
                    baseUrl: source.baseUrl,
                    apiPrefix: source.apiPrefix,
                    apiSuffix: source.apiSuffix,
                    serviceName: source.service + parameters.id,
                    method: 'GET',
                    parameters: parameters
                })
            }
            else {
                self._bridge.callResourceInstance(service, {
                    baseUrl: source.baseUrl,
                    apiPrefix: source.apiPrefix,
                    apiSuffix: source.apiSuffix,
                    serviceName: source.service,
                    method: 'GET',
                    parameters: parameters
                })
            }
        }
    },
    /**
     * @private
     * Delete the data record via the id.
     */
    deleteRecord: function(target, data) {
         // console.log('deleteRecord', target, data)
        const self = this
        const convertor = self.p_convertor()
        const wrapper = DataConvertor.wrapper(convertor)
        const source = convertor.view.remove
        const service = 'remove' + ObjectFunc.capitalizeFirstLetter(wrapper)
        const parameters = {id: self._bridge.getRouteParam('id')}
        if (data) {
            ObjectFunc.mergeObject(parameters, data)
        }
        const postBody = DataConvertor.transform(convertor, parameters, 'remove')
        // console.log('@parameters', parameters)
        self._bridge.addResourceListener(service, function(e) {
            self._bridge.removeResourceListener(service, e.handler)
            // console.log('remove.data', service, e.data)
            self.handleRemoveService(e)
        })
        if (source.apiSuffix && source.apiSuffix.indexOf('bmb-resource-service') > -1) {
            self._bridge.callResourceInstance(service, {
                baseUrl: source.baseUrl,
                apiPrefix: source.apiPrefix,
                apiSuffix: source.apiSuffix,
                serviceName: source.service + postBody.id,
                method: 'DELETE',
                data: postBody
            })
        }
        else {
            self._bridge.callResourceInstance(service, {
                baseUrl: source.baseUrl,
                apiPrefix: source.apiPrefix,
                apiSuffix: source.apiSuffix,
                serviceName: source.service,
                method: 'POST',
                data: postBody
            })
        }
    },
    submitData: function(target, data, isSaved) {
        // // console.log('submitData', target, data)
        if (isSaved) {
            GlobalFunc.clearExecute(this)
            return this.doSubmitData(target, data, isSaved)
        }

        if (this._bridge.isCreate()) {
            this.doSubmitData(target, data)
        }
        else if (this._bridge.isBatch()) {
            // check the empty item existed or not
            if (this.target().isEdit) {
                this.checkEmptyItemExisted(data)
                GlobalFunc.delayExecute(this, 'doSubmitBatchData', 1000, [target, data])
            }
            else {
                this.doSubmitBatchData(target, data)
            }
        }
        else {
            GlobalFunc.delayExecute(this, 'doSubmitData', 1000, [target, data])
        }
    },
    checkEmptyItemExisted: function(data) {
         console.log('checkEmptyItemExisted', data)
    },
    doSubmitBatchData: function(target, data) {
         console.log('doSubmitBatchData', target, data)
    },
    /**
     * @private
     * Create the data record if no id there.
     * Update the data record if id existed.
     */
    doSubmitData: function(target, data, isSaved) {
         // console.log('doSubmitData', target, data, isSaved)
        const self = this
        const convertor = self.p_convertor()
        const wrapper = DataConvertor.wrapper(convertor)
        const isCreate = self._bridge.isCreate()
        // compare the submit data
        let isEqual = true
        let submitData = {}
        this._prevSubmitData = this._prevSubmitData || {}
        const prevSubmitData = this._prevSubmitData
        for (let p in data) {
            // console.log('compare prev and cur', p, prevSubmitData[p] == data[p])
            if (data[p] != prevSubmitData[p]) {
                isEqual = false
                submitData[p] = data[p]
            }
            prevSubmitData[p] = data[p]
        }
        // console.log('isEqual', isEqual)
        if (isEqual) {
            if (isSaved) {
                self._bridge.triggerRouteBack()
            }
            return
        }
        // console.log('submitData', submitData, isCreate, isSaved)
        const type = isCreate ? 'create' : 'update'
        // use the original data for the bmb backend service
        const postBody = DataConvertor.transform(convertor, data, type)
        const source = convertor.view[type]
        const service = type + ObjectFunc.capitalizeFirstLetter(wrapper)
        self._bridge.addResourceListener(service, function(e) {
            self._bridge.removeResourceListener(service, e.handler)
            // console.log('service response data', service, e.data)
            if (type == 'create') {
                self.handleCreateService(e)
            }
            else if (type == 'update') {
                self.handleUpdateService(e)
                if (isSaved) {
                    self._bridge.triggerRouteBack()
                }
            }
        })
        // console.log('before service call', service, postBody)
        if (source.apiSuffix && source.apiSuffix.indexOf('bmb-resource-service') > -1 && type == 'update') {
            self._bridge.callResourceInstance(service, {
                baseUrl: source.baseUrl,
                apiPrefix: source.apiPrefix,
                apiSuffix: source.apiSuffix,
                serviceName: source.service + postBody.id,
                method: 'PUT',
                data: postBody
            })
        }
        else {
            self._bridge.callResourceInstance(service, {
                baseUrl: source.baseUrl,
                apiPrefix: source.apiPrefix,
                apiSuffix: source.apiSuffix,
                serviceName: source.service,
                method: 'POST',
                data: postBody
            })
        } 
    },
    handleRemoveService: function(e) {
        if (e.data.result == true) {
            this._bridge.back()
        }
    },
    handleCreateService: function(e) {
        if (e.data.result == true) {
            // console.log('handleCreateService validate cached', this.properties().cached)
            if (this.properties().cached) {
                // console.log('clear the status', this._bridge.getRouteName())
                ModelLocator.setStatus(this._bridge.getRouteName(), null)
            }
            this._bridge.back()
        }
    },
    handleUpdateService: function() {
    },
    handleQueryService: function(e) {
        // console.log('handleQueryService', e)
        const self = this
        const convertor = self.p_convertor()
        if (!convertor) {
            return
        }
        let type 
        type = self._bridge.isList() ? 'query' : 'get'
        type = self._bridge.isManage() ? 'manage' : type
        const isCollection = self._bridge.isCollection()
        if (e.data.result == true) {
            let entityData = e.data.payload
            if (ClassFunc.getClass(entityData) == "String") {
                if (type == 'get') {
                    entityData  = {}
                }
                else if (type == 'query') {
                    entityData = []
                }
            }
            const viewData = DataConvertor.transform(convertor, entityData, type, e.data.options)
            const wrapper = DataConvertor.wrapper(convertor, isCollection)
            // console.log('@wrapper-viewData', wrapper, viewData)
            if (self.handleDataWrapped(viewData, wrapper)) {
                if (!wrapper) {
                    for (let p in viewData) {
                        self.target()[p] = viewData[p]
                    }
                }
                else {
                    self.target()[wrapper] = viewData
                }
            }
            // console.log('@before handleDataLoaded')
            self.handleDataLoaded(e)
        }
    },
    handleDataWrapped: function() {
        return true
    },
    handleDataLoad: function(parameters) {
        // console.log('@BaseMediator.handleDataLoad', parameters)
        return parameters
    },
    handleDataLoaded: function() {
        // console.log('@BaseMediator.handleDataLoaded')
        if (this.target().isRoot) {
            this.target().parentUpdated()
        }
    },
    handleQueryCollectionService: function(e) {
        // console.log('handleQueryCollectionService', e)
        const self = this
        const convertor = self.p_convertor()
        let type 
        type = self._bridge.isList() ? 'query' : 'get'
        type = self._bridge.isManage() ? 'manage' : type
        let viewData = {}
        let dataSets = e.data.dataSets
        let transformedDataSets = []
        for (let i = 0; i < dataSets.length; i++) {
            let dataSet = dataSets[i]
            if (dataSet.result) {
                let item = convertor.view[type][i]
                // console.log('dataSet', dataSet)
                const transformedData = DataConvertor.doTransform(convertor, item.basePath, item.rule, dataSet.payload, type, dataSet.options, transformedDataSets)
                transformedDataSets.push(transformedData)
                if (item.name) {
                    if (viewData[item.name]) {
                        ObjectFunc.mergeObject(viewData[item.name], transformedData)
                    }
                    else {
                        viewData[item.name] = transformedData
                    }
                }
                else {
                    ObjectFunc.mergeObject(viewData, transformedData)
                }
            }
        }
        const wrapper = DataConvertor.wrapper(convertor)
        // // console.log('@wrapper-viewData', wrapper, viewData)
        // // console.log('@target', self.target(), wrapper)
        if (self.handleViewDataWrap(viewData)) {
            if (wrapper) {
                // console.log('@wrapper-viewData', wrapper, viewData)
                self.target()[wrapper] = viewData
            }
            else {
                for (let p in viewData) {
                    // console.log('@wrapper-viewData', p, viewData[p])
                    self.target()[p] = viewData[p]
                }
            }
        }
        self.handleDataLoaded(e)
    },
    handleViewDataWrap: function() {
        return true
    },
    fetchCityRegion: function() {
        // console.log('fetchCityRegion')
        const self = this
        const cityRegionConvertor = new CityRegionConvertor()
        const source = cityRegionConvertor.view.query
        const service = 'queryCityRegion'
        self._bridge.addResourceListener(service, function(e) {
            self._bridge.removeResourceListener(service, e.handler)
            // console.log('queryCityRegion.data', e.data)
            if (e.data.result == true) {
                const viewData = DataConvertor.transform(cityRegionConvertor, e.data.payload, 'query')
                // console.log('viewData', viewData)
                const wrapper = DataConvertor.wrapper(cityRegionConvertor)
                // console.log('wrapper', wrapper)
                self.target()[wrapper] = viewData
            }
        })
        self._bridge.callResourceInstance(service, {
            baseUrl: source.baseUrl,
            apiPrefix: source.apiPrefix,
            apiSuffix: source.apiSuffix,
            serviceName: source.service,
            method: 'GET'
        })
    },
    exitPage: function() {
        // console.log('exit page via DSBridge')
        DSBridge.exitPage()
    },
    sendStaffInvite: function(data) {
        // console.log('sendStaffInvite', data)
        const self = this
        const staff = new Staff
        const source = staff.view.send
        const service = 'sendStaffInvite'
        const parameters = {
            contactId: data.id,
            orgId: data.organizationId
        }
        self._bridge.addResourceListener(service, function(e) {
            self._bridge.removeResourceListener(service, e.handler)
            // console.log('send.data', service, e.data)
            const staffInvite = DataConvertor.transform(staff, e.data.payload, 'send')
            const startIndex = location.href.indexOf('/org')
            const link = location.href.substr(0, startIndex) + '/org/staff/invite?ticket=' + staffInvite.ticket    
            const shareParam = {
                title: staff.localeProp('staff-invite-title', {role: data.roleLabel}),
                desc: staffInvite.organizationName,
                imgUrl: Utils.QNImageUtil.imageView(staffInvite.organizationLogoUrl, 360, 360),
                link: link
            }
            // console.log('share link', shareParam)
            DSBridge.callShare(shareParam)
        })
        self._bridge.callResourceInstance(service, {
            baseUrl: source.baseUrl,
            apiPrefix: source.apiPrefix,
            apiSuffix: source.apiSuffix,
            serviceName: source.service,
            method: 'GET',
            parameters: parameters
        })
    },
    loadViewObjectData: function(convertor, parameters, callback, type) {
        const self = this
        type = type || 'get'
        const source = convertor.view[type]
        const wrapper = DataConvertor.wrapper(convertor)
        const service = type + ObjectFunc.capitalizeFirstLetter(wrapper)
        parameters = DataConvertor.queryTransform(convertor, parameters, type)
        if (ClassFunc.getClass(source) == 'Array') {
            self._bridge.addResourceListener(service, function(e) {
                self._bridge.removeResourceListener(service, e.handler)
                // console.log('collection.get.data', service, e.data)
                let viewData = {}
                let dataSets = e.data.dataSets
                for (let i = 0; i < dataSets.length; i++) {
                    let dataSet = dataSets[i]
                    if (dataSet.result) {
                        let item = convertor.view[type][i]
                        // // console.log('dataSet', dataSet)
                        const transformedData = DataConvertor.doTransform(convertor, item.basePath, item.rule, dataSet.payload, type)
                        if (viewData[item.name]) {
                            ObjectFunc.mergeObject(viewData[item.name], transformedData)
                        }
                        else {
                            viewData[item.name] = transformedData
                        }
                    }
                }
                callback && callback(viewData)
            })
            self._bridge.callResourceInstance(service, {
                services: ObjectFunc.extractProperties(source, ['baseUrl', 'apiPrefix', 'apiSuffix', 'service']),
                method: 'GET',
                parameters: parameters
            }, true)
        }
        else {
            self._bridge.addResourceListener(service, function(e) {
                self._bridge.removeResourceListener(service, e.handler)
                // console.log('single.get.data', service, e.data)
                const viewData = DataConvertor.transform(convertor, e.data.payload, type, e.data.options)
                // // console.log('viewData', viewData)
                callback && callback(viewData)
            })
            if (source.apiSuffix && source.apiSuffix.indexOf('bmb-resource-service') > -1 && type == 'get') {
                self._bridge.callResourceInstance(service, {
                    baseUrl: source.baseUrl,
                    apiPrefix: source.apiPrefix,
                    apiSuffix: source.apiSuffix,
                    serviceName: source.service + parameters.id,
                    method: 'GET',
                    parameters: parameters
                })
            }
            else {
                self._bridge.callResourceInstance(service, {
                    baseUrl: source.baseUrl,
                    apiPrefix: source.apiPrefix,
                    apiSuffix: source.apiSuffix,
                    serviceName: source.service,
                    method: 'GET',
                    parameters: parameters
                })
            }
        }
    },
    commitViewObjectData: function(convertor, parameters, callback, type) {
        const self = this
        type = type || 'create'
        const postBody = DataConvertor.transform(convertor, parameters, type)
        const source = convertor.view[type]
        const wrapper = DataConvertor.wrapper(convertor)
        const service = type + ObjectFunc.capitalizeFirstLetter(wrapper)
        self._bridge.addResourceListener(service, function(e) {
            self._bridge.removeResourceListener(service, e.handler)
            // console.log('service response data', service, e.data)
            callback && callback(e)
        })
        // console.log('before service call', service, postBody)
        if (source.apiSuffix && source.apiSuffix.indexOf('bmb-resource-service') > -1 && type == 'update') {
            self._bridge.callResourceInstance(service, {
                baseUrl: source.baseUrl,
                apiPrefix: source.apiPrefix,
                apiSuffix: source.apiSuffix,
                serviceName: source.service + postBody.id,
                method: 'PUT',
                data: postBody
            })
        }
        else {
            self._bridge.callResourceInstance(service, {
                baseUrl: source.baseUrl,
                apiPrefix: source.apiPrefix,
                apiSuffix: source.apiSuffix,
                serviceName: source.service,
                method: 'POST',
                data: postBody
            })
        } 
    },
}

//--------------------------------------------------
// extends the AbstractMediator
//--------------------------------------------------
ClassFunc.extend(tbs_controller_BaseMediator, AbstractMediator);

module.exports = tbs_controller_BaseMediator